// Phase 8b — Tax Settings (4 collapsible cards w/ live calculators + plain-language law).
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, Save, RotateCcw, FileDown, Info, Banknote, Receipt, Briefcase, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import { Navigate } from "react-router-dom";

// Embedded defaults — mirrors backend ITA defaults so user can reset without round-trip.
const DEFAULTS = {
  paye_brackets: [
    { min: 0.0, max: 100.0, rate: 0.0 },
    { min: 100.01, max: 500.0, rate: 0.06 },
    { min: 500.01, max: 1500.0, rate: 0.12 },
    { min: 1500.01, max: null, rate: 0.18 },
  ],
  paye_non_resident_rate: 0.18,
  paye_under_18_rate: 0.20,
  paye_filing_day: 15,
  sales_tax_rate: 0.05,
  wht_categories: {
    rental_immovable: 0.05, rental_movable: 0.10, capital_gains: 0.15,
    trust_income: 0.26, dividends: 0.05, murabaha: 0.10,
    royalties: 0.15, service_fees: 0.10, insurance_premium: 0.05,
  },
  corporate_tiers: [
    { sales_min: 0.0, sales_max: 10000.0, type: "fixed", value: 150.0 },
    { sales_min: 10000.01, sales_max: 50000.0, type: "pct_sales", value: 0.015 },
    { sales_min: 50000.01, sales_max: null, type: "pct_profit", value: 0.15 },
  ],
  corporate_non_resident_rate: 0.18,
};

const $ = (v) => `$${Number(v||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const pct = (v) => `${(Number(v||0)*100).toFixed(2)}%`;

function computePAYE(salary, brackets) {
  const s = Number(salary) || 0;
  if (s <= 0) return 0;
  let tax = 0;
  for (const b of brackets || []) {
    const lo = Number(b.min);
    const hi = b.max === null || b.max === undefined || b.max === "" ? Infinity : Number(b.max);
    if (s < lo) continue;
    const upper = Math.min(s, hi);
    const slice = Math.max(0, upper - (lo === 0 ? 0 : lo - 0.01));
    if (slice > 0) tax += slice * Number(b.rate);
  }
  return Math.round(tax * 100) / 100;
}

function pickTier(sales, profit, tiers) {
  const s = Number(sales) || 0;
  const p = Number(profit) || 0;
  for (const t of tiers || []) {
    const lo = Number(t.sales_min);
    const hi = t.sales_max === null || t.sales_max === undefined ? Infinity : Number(t.sales_max);
    if (s >= lo && s <= hi) {
      if (t.type === "fixed") return { tier: t, liability: Number(t.value) };
      if (t.type === "pct_sales") return { tier: t, liability: s * Number(t.value) };
      if (t.type === "pct_profit") return { tier: t, liability: Math.max(0, p) * Number(t.value) };
    }
  }
  return { tier: null, liability: 0 };
}

function HelpBanner({ children }) {
  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 text-sky-900 p-3 flex items-start gap-2 text-[13px] leading-relaxed">
      <Info className="h-4 w-4 mt-0.5 shrink-0 opacity-70" /><div>{children}</div>
    </div>
  );
}

function Footer({ s }) {
  const upd = s?.updated_at?.slice(0, 19).replace("T", " ");
  return <p className="text-xs text-slate-500">Last updated: <strong>{upd || "—"}</strong> by <strong>{s?.updated_by || "seed"}</strong></p>;
}

export default function TaxSettingsPage() {
  const { user } = useAuth();
  // Bug bundle #12 (2026-08-19) — Full Access override bypasses the
  // static role allow-list (system_admin, finance_manager).
  const allowed = [ROLES.SYSTEM_ADMIN, ROLES.SALIHI_FINANCE_MANAGER].includes(user?.role)
                  || user?.full_access === true;
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // "paye" | "vat" | "wht" | "cit" | "all"

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    api.get("/tax-settings")
      .then(r => { setS(r.data); setLoading(false); })
      .catch(e => { toast.error(formatApiError(e)); setLoading(false); });
  }, [allowed]);

  if (!allowed) return <Navigate to="/dashboard" replace />;
  if (loading || !s) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#4FA3D1]" /></div>;

  async function save(scope, patch) {
    setSaving(scope);
    try {
      const r = await api.patch("/tax-settings", patch);
      setS(r.data);
      toast.success("Tax settings updated (audit-logged).");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  }

  function resetToDefaults(scope) {
    const map = {
      paye: { paye_brackets: DEFAULTS.paye_brackets, paye_non_resident_rate: DEFAULTS.paye_non_resident_rate, paye_under_18_rate: DEFAULTS.paye_under_18_rate, paye_filing_day: DEFAULTS.paye_filing_day },
      vat:  { sales_tax_rate: DEFAULTS.sales_tax_rate },
      wht:  { wht_categories: DEFAULTS.wht_categories },
      cit:  { corporate_tiers: DEFAULTS.corporate_tiers, corporate_non_resident_rate: DEFAULTS.corporate_non_resident_rate },
    };
    setS({ ...s, ...map[scope] });
    toast.message(`${scope.toUpperCase()} fields reset locally. Click Save to persist.`);
  }

  function printSummary() {
    const token = localStorage.getItem("st_token");
    fetch(`${API}/tax-settings/summary.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => {
        if (!r.ok) { toast.error("Failed to download summary."); return; }
        const b = await r.blob(); const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = "SOMTURK-Tax-Settings-Summary.pdf"; a.click();
        URL.revokeObjectURL(a.href);
      });
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="tax-settings-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold">Tax Settings</h2>
          <p className="text-sm text-slate-500 mt-1">
            Configure tax rates and rules per Somali tax law. Editable by Admin and Finance Manager — every change is audit-logged.
          </p>
        </div>
        <Button variant="outline" onClick={printSummary} data-testid="print-tax-summary-btn">
          <FileDown className="h-4 w-4 mr-1.5" /> Print Tax Settings Summary
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={["paye"]} className="space-y-3">
        {/* ---------------- PAYE ---------------- */}
        <AccordionItem value="paye" className="border rounded-lg bg-white px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="paye-card-trigger">
            <div className="flex items-center gap-3">
              <Banknote className="h-5 w-5 text-sky-600" />
              <div className="text-left">
                <p className="font-semibold text-slate-900">1. Payroll Tax (PAYE) — Monthly</p>
                <p className="text-xs text-slate-500">Progressive brackets · Filing day {s.paye_filing_day}</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <div className="space-y-4">
              <HelpBanner>
                <strong>Plain-language:</strong> Each month the employer holds back a portion of the employee’s salary as tax and pays it to the tax authority. The rate increases for higher salaries (progressive). Filing is due by day <strong>{s.paye_filing_day}</strong> of the following month. Non-resident employees pay a flat rate; employees under 18 also pay a flat rate.
              </HelpBanner>
              <PAYEEditor s={s} setS={setS} />
              <PAYECalculator brackets={s.paye_brackets} />
              <div className="flex items-center justify-between pt-2 border-t flex-wrap gap-2">
                <Footer s={s} />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => resetToDefaults("paye")} data-testid="reset-paye">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to defaults
                  </Button>
                  <Button size="sm" disabled={saving === "paye"} onClick={() => save("paye", {
                    paye_brackets: s.paye_brackets, paye_non_resident_rate: s.paye_non_resident_rate,
                    paye_under_18_rate: s.paye_under_18_rate, paye_filing_day: s.paye_filing_day,
                  })} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="save-paye">
                    {saving === "paye" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                    Save PAYE
                  </Button>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ---------------- VAT ---------------- */}
        <AccordionItem value="vat" className="border rounded-lg bg-white px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="vat-card-trigger">
            <div className="flex items-center gap-3">
              <Receipt className="h-5 w-5 text-emerald-600" />
              <div className="text-left">
                <p className="font-semibold text-slate-900">2. Sales Tax (VAT)</p>
                <p className="text-xs text-slate-500">Standard rate: <Badge variant="secondary" className="ml-1">{pct(s.sales_tax_rate)}</Badge></p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <div className="space-y-4">
              <HelpBanner>
                <strong>Plain-language:</strong> A flat tax on the net value of every taxable sale of goods or services. You add the VAT amount on top of your invoice and pass it to the tax authority each month.
              </HelpBanner>
              <div className="flex items-center gap-3">
                <Label className="text-sm">Standard rate (e.g. 0.05 = 5%)</Label>
                <Input type="number" step="0.001" value={s.sales_tax_rate}
                       onChange={e => setS({ ...s, sales_tax_rate: parseFloat(e.target.value) })}
                       className="w-32" data-testid="sales-tax-rate" />
                <span className="text-sm text-slate-500">= <strong>{pct(s.sales_tax_rate)}</strong></span>
              </div>
              <VATCalculator rate={s.sales_tax_rate} />
              <div className="flex items-center justify-between pt-2 border-t flex-wrap gap-2">
                <Footer s={s} />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => resetToDefaults("vat")} data-testid="reset-vat">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to defaults
                  </Button>
                  <Button size="sm" disabled={saving === "vat"} onClick={() => save("vat", { sales_tax_rate: s.sales_tax_rate })}
                          className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="save-vat">
                    {saving === "vat" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                    Save VAT
                  </Button>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ---------------- WHT ---------------- */}
        <AccordionItem value="wht" className="border rounded-lg bg-white px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="wht-card-trigger">
            <div className="flex items-center gap-3">
              <Briefcase className="h-5 w-5 text-amber-600" />
              <div className="text-left">
                <p className="font-semibold text-slate-900">3. Withholding Tax (WHT)</p>
                <p className="text-xs text-slate-500">{Object.keys(s.wht_categories||{}).length} categories</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <div className="space-y-4">
              <HelpBanner>
                <strong>Plain-language:</strong> When you pay a supplier for certain things (rent, royalties, services, dividends, etc.), you keep back a small percentage and send it to the tax authority on the supplier’s behalf. The supplier receives a certificate that they use to credit their final tax bill.
              </HelpBanner>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(s.wht_categories || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <Label className="flex-1 text-xs capitalize">{k.replace(/_/g, " ")}</Label>
                    <Input type="number" step="0.001" value={v}
                           onChange={e => setS({ ...s, wht_categories: { ...s.wht_categories, [k]: parseFloat(e.target.value) } })}
                           className="w-24" data-testid={`wht-${k}`} />
                    <span className="text-xs text-slate-500 w-12 text-right">{pct(v)}</span>
                  </div>
                ))}
              </div>
              <WHTCalculator categories={s.wht_categories} />
              <div className="flex items-center justify-between pt-2 border-t flex-wrap gap-2">
                <Footer s={s} />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => resetToDefaults("wht")} data-testid="reset-wht">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to defaults
                  </Button>
                  <Button size="sm" disabled={saving === "wht"} onClick={() => save("wht", { wht_categories: s.wht_categories })}
                          className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="save-wht">
                    {saving === "wht" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                    Save WHT
                  </Button>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ---------------- Corporate ---------------- */}
        <AccordionItem value="cit" className="border rounded-lg bg-white px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="cit-card-trigger">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-violet-600" />
              <div className="text-left">
                <p className="font-semibold text-slate-900">4. Corporate Income Tax</p>
                <p className="text-xs text-slate-500">{(s.corporate_tiers||[]).length} tiers · Non-resident: {pct(s.corporate_non_resident_rate)}</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <div className="space-y-4">
              <HelpBanner>
                <strong>Plain-language:</strong> Corporate tax depends on your annual sales — a small business pays a fixed fee, a medium business pays a percentage of sales, and a larger business pays a percentage of net profit. Quarterly installments are due Mar 31, Jun 30, Sep 30, and Dec 31. Final annual return is due Mar 31.
              </HelpBanner>
              <CITEditor s={s} setS={setS} />
              <CITCalculator tiers={s.corporate_tiers} />
              <div className="flex items-center justify-between pt-2 border-t flex-wrap gap-2">
                <Footer s={s} />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => resetToDefaults("cit")} data-testid="reset-cit">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to defaults
                  </Button>
                  <Button size="sm" disabled={saving === "cit"} onClick={() => save("cit", {
                    corporate_tiers: s.corporate_tiers, corporate_non_resident_rate: s.corporate_non_resident_rate,
                  })} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="save-cit">
                    {saving === "cit" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                    Save Corporate
                  </Button>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

/* ----- PAYE Editor ----- */
function PAYEEditor({ s, setS }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-700">Monthly brackets</p>
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2 text-xs uppercase text-slate-500 px-1">
          <span>From ($)</span><span>To ($ or blank for ∞)</span><span>Rate (decimal)</span>
        </div>
        {(s.paye_brackets||[]).map((b, i) => (
          <div key={i} className="grid grid-cols-3 gap-2">
            <Input type="number" step="0.01" value={b.min}
                   onChange={e => { const x=[...s.paye_brackets]; x[i]={...x[i],min:parseFloat(e.target.value)}; setS({...s,paye_brackets:x});}}
                   data-testid={`paye-min-${i}`} />
            <Input type="number" step="0.01" value={b.max||""} placeholder="∞"
                   onChange={e => { const x=[...s.paye_brackets]; x[i]={...x[i],max:e.target.value?parseFloat(e.target.value):null}; setS({...s,paye_brackets:x});}} />
            <Input type="number" step="0.001" value={b.rate}
                   onChange={e => { const x=[...s.paye_brackets]; x[i]={...x[i],rate:parseFloat(e.target.value)}; setS({...s,paye_brackets:x});}}
                   data-testid={`paye-rate-${i}`} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 pt-1">
        <div><Label className="text-xs">Non-resident rate</Label>
          <Input type="number" step="0.001" value={s.paye_non_resident_rate}
                 onChange={e=>setS({...s,paye_non_resident_rate:parseFloat(e.target.value)})}/></div>
        <div><Label className="text-xs">Under-18 rate</Label>
          <Input type="number" step="0.001" value={s.paye_under_18_rate}
                 onChange={e=>setS({...s,paye_under_18_rate:parseFloat(e.target.value)})}/></div>
        <div><Label className="text-xs">Filing day-of-month</Label>
          <Input type="number" value={s.paye_filing_day}
                 onChange={e=>setS({...s,paye_filing_day:parseInt(e.target.value)})}/></div>
      </div>
    </div>
  );
}

/* ----- PAYE Calculator ----- */
function PAYECalculator({ brackets }) {
  const [salary, setSalary] = useState(1200);
  const tax = computePAYE(salary, brackets);
  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3" data-testid="paye-calculator">
      <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Live example</p>
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm">Monthly gross salary:</Label>
        <Input type="number" value={salary} onChange={e=>setSalary(e.target.value)}
               className="w-32" data-testid="paye-calc-input" />
        <span className="text-sm text-slate-500">→</span>
        <span className="text-sm font-semibold text-slate-900" data-testid="paye-calc-output">
          PAYE owed = {$(tax)} · Net pay = {$(Number(salary||0) - tax)}
        </span>
      </div>
    </div>
  );
}

/* ----- VAT Calculator ----- */
function VATCalculator({ rate }) {
  const [net, setNet] = useState(1000);
  const vat = (Number(net)||0) * (Number(rate)||0);
  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3" data-testid="vat-calculator">
      <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Live example</p>
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm">Invoice net amount:</Label>
        <Input type="number" value={net} onChange={e=>setNet(e.target.value)}
               className="w-32" data-testid="vat-calc-input" />
        <span className="text-sm text-slate-500">→</span>
        <span className="text-sm font-semibold text-slate-900" data-testid="vat-calc-output">
          VAT = {$(vat)} · Total = {$((Number(net)||0)+vat)}
        </span>
      </div>
    </div>
  );
}

/* ----- WHT Calculator ----- */
function WHTCalculator({ categories }) {
  const cats = Object.keys(categories || {});
  const [cat, setCat] = useState(cats[0] || "service_fees");
  const [gross, setGross] = useState(1000);
  const rate = Number(categories?.[cat] || 0);
  const wht = (Number(gross)||0) * rate;
  const net = (Number(gross)||0) - wht;
  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3" data-testid="wht-calculator">
      <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Live example</p>
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm">Category:</Label>
        <select value={cat} onChange={e=>setCat(e.target.value)}
                className="px-2 py-1.5 rounded-md border border-slate-300 text-sm bg-white"
                data-testid="wht-calc-category">
          {cats.map(c=><option key={c} value={c}>{c.replace(/_/g," ")}</option>)}
        </select>
        <Label className="text-sm">Payment ($):</Label>
        <Input type="number" value={gross} onChange={e=>setGross(e.target.value)}
               className="w-32" data-testid="wht-calc-input" />
        <span className="text-sm text-slate-500">→</span>
        <span className="text-sm font-semibold text-slate-900" data-testid="wht-calc-output">
          WHT @ {pct(rate)} = {$(wht)} · Net to supplier = {$(net)}
        </span>
      </div>
    </div>
  );
}

/* ----- CIT Editor + Calculator ----- */
function CITEditor({ s, setS }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-700">Tiers (by annual sales)</p>
      <div className="grid grid-cols-4 gap-2 text-xs uppercase text-slate-500 px-1">
        <span>Sales from ($)</span><span>Sales to ($)</span><span>Tier type</span><span>Value</span>
      </div>
      {(s.corporate_tiers||[]).map((t,i)=>(
        <div key={i} className="grid grid-cols-4 gap-2 items-center">
          <Input type="number" value={t.sales_min}
                 onChange={e=>{const x=[...s.corporate_tiers];x[i]={...x[i],sales_min:parseFloat(e.target.value)};setS({...s,corporate_tiers:x});}}/>
          <Input type="number" value={t.sales_max||""} placeholder="∞"
                 onChange={e=>{const x=[...s.corporate_tiers];x[i]={...x[i],sales_max:e.target.value?parseFloat(e.target.value):null};setS({...s,corporate_tiers:x});}}/>
          <Badge variant="secondary">{t.type}</Badge>
          <Input type="number" step="0.001" value={t.value}
                 onChange={e=>{const x=[...s.corporate_tiers];x[i]={...x[i],value:parseFloat(e.target.value)};setS({...s,corporate_tiers:x});}}/>
        </div>
      ))}
      <div className="pt-1">
        <Label className="text-xs">Non-resident corporate rate</Label>
        <Input type="number" step="0.001" value={s.corporate_non_resident_rate}
               onChange={e=>setS({...s,corporate_non_resident_rate:parseFloat(e.target.value)})}
               className="w-32"/>
      </div>
    </div>
  );
}

function CITCalculator({ tiers }) {
  const [sales, setSales] = useState(75000);
  const [profit, setProfit] = useState(15000);
  const { tier, liability } = pickTier(sales, profit, tiers);
  const tierLabel = !tier ? "No matching tier" :
    tier.type === "fixed" ? `Fixed-fee tier (${$(tier.value)})` :
    tier.type === "pct_sales" ? `% of sales tier (${pct(tier.value)} × sales)` :
    `% of profit tier (${pct(tier.value)} × net profit)`;
  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3" data-testid="cit-calculator">
      <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Live example</p>
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm">Annual sales:</Label>
        <Input type="number" value={sales} onChange={e=>setSales(e.target.value)} className="w-32" data-testid="cit-calc-sales" />
        <Label className="text-sm">Net profit:</Label>
        <Input type="number" value={profit} onChange={e=>setProfit(e.target.value)} className="w-32" data-testid="cit-calc-profit" />
      </div>
      <p className="mt-2 text-sm" data-testid="cit-calc-output">
        Your business is currently in: <strong>{tierLabel}</strong> · Tax liability: <strong>{$(liability)}</strong>
      </p>
    </div>
  );
}
